// src/storage/scheduleAdvisor.ts — Context-Aware Practice Advisor & Chronometer (FR-REC-001..003)
// Evaluates time since last practice, time of day (tide phases), day of week,
// and available duration to suggest optimal, ADHD-ergonomic learning activities.

import { StreakManager } from './streakManager';
import { SessionGuard } from './sessionGuard';
import { SprintStorage } from './sprintStorage';
import { ComprehensionStorage } from './comprehensionStorage';
import { MockExamStorage } from './mockExamStorage';

export type TidePhase = 'morning' | 'afternoon' | 'evening' | 'night';
export type DayType = 'weekday' | 'saturday' | 'sunday';
export type SessionDurationMinutes = 5 | 10 | 20 | 45;

export interface TidePhaseInfo {
  phase: TidePhase;
  label: string;
  badge: string;
  icon: string;
  cognitiveNote: string;
}

export interface ActivityRecommendation {
  stationRoute: string;
  stationName: string;
  domainLabel: string;
  title: string;
  description: string;
  rationale: string;
  estimatedMinutes: SessionDurationMinutes;
  explorerPoints: number;
  claweyQuote: string;
  urgency: 'routine' | 'recovery' | 'peak' | 'rest';
}

export interface PracticeContext {
  lastActiveTimestamp: number;
  timeSinceLastPracticeMs: number;
  timeSinceLastPracticeFormatted: string;
  isFirstEverSession: boolean;
  tidePhase: TidePhaseInfo;
  dayType: DayType;
  dayName: string;
  todayPracticeMinutes: number;
  dailyLimitMinutes: number;
  isDailyLimitReached: boolean;
  streakStatus: {
    currentStreak: number;
    isFrozen: boolean;
  };
}

export const TIDE_PHASE_INFO: Record<TidePhase, TidePhaseInfo> = {
  morning: {
    phase: 'morning',
    label: 'Morning Low Tide',
    badge: '🌅 Morning Low Tide',
    icon: '🌅',
    cognitiveNote:
      'Alertness Peak: Ideal for quick vocabulary retrieval & phonics warm-ups before school.',
  },
  afternoon: {
    phase: 'afternoon',
    label: 'Afternoon High Tide',
    badge: '⚡ Afternoon High Tide',
    icon: '⚡',
    cognitiveNote:
      'Active Reasoning Window: Optimal for focused 10-minute Quest Sprints and homework diagnosis.',
  },
  evening: {
    phase: 'evening',
    label: 'Evening Abyssal Calm',
    badge: '🌙 Evening Abyssal Calm',
    icon: '🌙',
    cognitiveNote:
      'Gentle Focus: Calming comprehension clue-hunting and tactile geometry without intense timer stress.',
  },
  night: {
    phase: 'night',
    label: 'Night Watch & Rest',
    badge: '🌌 Night Watch & Rest',
    icon: '🌌',
    cognitiveNote:
      'Cognitive Restoration: Sleep consolidates memory. Gentle specimen viewing or breathing breaks recommended.',
  },
};

export class ScheduleAdvisor {
  /**
   * Scans all persistent storage records to determine the true latest activity timestamp.
   */
  static getLatestPracticeTimestamp(): number {
    let latest = 0;

    // 1. Streak Manager last active
    try {
      const streakData = StreakManager.load();
      if (streakData.lastActiveTimestamp > latest) {
        latest = streakData.lastActiveTimestamp;
      }
    } catch {
      // Ignore
    }

    // 2. Sprint Storage sessions
    try {
      const sprintProgress = SprintStorage.load();
      const sprintSessions = sprintProgress.sessions;
      if (sprintSessions.length > 0) {
        const lastSprint = sprintSessions[sprintSessions.length - 1];
        if (lastSprint && lastSprint.timestamp > latest) {
          latest = lastSprint.timestamp;
        }
      }
    } catch {
      // Ignore
    }

    // 3. Comprehension Storage sessions
    try {
      const compProgress = ComprehensionStorage.load();
      const lastClue = compProgress.clueAttempts[compProgress.clueAttempts.length - 1];
      if (lastClue && lastClue.timestamp > latest) {
        latest = lastClue.timestamp;
      }
      const lastVocab = compProgress.vocabAttempts[compProgress.vocabAttempts.length - 1];
      if (lastVocab && lastVocab.timestamp > latest) {
        latest = lastVocab.timestamp;
      }
      const lastRet = compProgress.retrievalSessions[compProgress.retrievalSessions.length - 1];
      if (lastRet && lastRet.timestamp > latest) {
        latest = lastRet.timestamp;
      }
    } catch {
      // Ignore
    }

    // 4. Mock Exam Storage attempts
    try {
      const mockAttempts = MockExamStorage.loadRecords();
      if (mockAttempts.length > 0) {
        const lastMock = mockAttempts[0];
        if (lastMock && lastMock.timestamp > latest) {
          latest = lastMock.timestamp;
        }
      }
    } catch {
      // Ignore
    }

    return latest;
  }

  /**
   * Formats elapsed time into a child- and parent-friendly string.
   */
  static formatElapsedRecency(elapsedMs: number, hasNeverPracticed: boolean): string {
    if (hasNeverPracticed || elapsedMs < 0) {
      return 'First dive in the Atelier! Welcome aboard.';
    }

    const minutes = Math.floor(elapsedMs / (60 * 1000));
    const hours = Math.floor(elapsedMs / (60 * 60 * 1000));
    const days = Math.floor(elapsedMs / (24 * 60 * 60 * 1000));

    if (minutes < 1) {
      return 'Just now (< 1 minute ago)';
    }
    if (minutes < 60) {
      return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    }
    if (hours < 24) {
      return `${hours} hour${hours === 1 ? '' : 's'} ago today`;
    }
    if (hours < 48) {
      return 'Yesterday (~' + hours + ' hours ago)';
    }
    return `${days} days ago (Streak paused safely ⚓)`;
  }

  /**
   * Determines the current circadian tide phase.
   */
  static getTidePhase(date = new Date()): TidePhaseInfo {
    const hour = date.getHours();
    if (hour >= 6 && hour < 12) {
      return TIDE_PHASE_INFO.morning;
    }
    if (hour >= 12 && hour < 17.5) {
      return TIDE_PHASE_INFO.afternoon;
    }
    if (hour >= 17.5 && hour < 21) {
      return TIDE_PHASE_INFO.evening;
    }
    return TIDE_PHASE_INFO.night;
  }

  /**
   * Identifies the day type (weekday vs weekend).
   */
  static getDayType(date = new Date()): { dayType: DayType; dayName: string } {
    const day = date.getDay();
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayName = dayNames[day] ?? 'Today';

    if (day === 0) return { dayType: 'sunday', dayName };
    if (day === 6) return { dayType: 'saturday', dayName };
    return { dayType: 'weekday', dayName };
  }

  /**
   * Assembles full contextual intelligence about the user's practice recency and schedule.
   */
  static getPracticeContext(now = new Date()): PracticeContext {
    const lastActiveTimestamp = this.getLatestPracticeTimestamp();
    const isFirstEverSession = lastActiveTimestamp === 0;
    const timeSinceLastPracticeMs = isFirstEverSession ? 0 : Math.max(0, now.getTime() - lastActiveTimestamp);
    const timeSinceLastPracticeFormatted = this.formatElapsedRecency(timeSinceLastPracticeMs, isFirstEverSession);

    const tidePhase = this.getTidePhase(now);
    const { dayType, dayName } = this.getDayType(now);

    const streakStatus = StreakManager.checkStatus();
    const todayPracticeMinutes = SessionGuard.getTodayPracticeMinutes();
    const dailyLimitMinutes = SessionGuard.getDailyLimitMinutes();
    const isDailyLimitReached = SessionGuard.isDailyLimitReached();

    return {
      lastActiveTimestamp,
      timeSinceLastPracticeMs,
      timeSinceLastPracticeFormatted,
      isFirstEverSession,
      tidePhase,
      dayType,
      dayName,
      todayPracticeMinutes,
      dailyLimitMinutes,
      isDailyLimitReached,
      streakStatus: {
        currentStreak: streakStatus.currentStreak,
        isFrozen: streakStatus.isFrozen,
      },
    };
  }

  /**
   * Determines the default duration based on current time and day.
   */
  static getDefaultDuration(now = new Date()): SessionDurationMinutes {
    const { dayType } = this.getDayType(now);
    const tide = this.getTidePhase(now).phase;

    if (dayType === 'saturday' && tide === 'morning') {
      return 20; // Weekend morning deep expedition
    }
    if (tide === 'morning') {
      return 5; // Quick morning dip before school
    }
    if (tide === 'night') {
      return 5; // Calm evening wind-down
    }
    return 10; // Standard 10-minute sprint
  }

  /**
   * Generates a primary contextual activity recommendation tailored to time of day,
   * day of week, recency, and chosen session length.
   */
  static getRecommendation(
    durationOverride?: SessionDurationMinutes,
    now = new Date()
  ): ActivityRecommendation {
    const context = this.getPracticeContext(now);
    const duration = durationOverride ?? this.getDefaultDuration(now);
    const { tidePhase, dayType, isDailyLimitReached, streakStatus } = context;

    // Special Case: Daily practice limit reached
    if (isDailyLimitReached && duration > 5) {
      return {
        stationRoute: '#/gallery',
        stationName: 'Naturalist Studio & Gallery',
        domainLabel: 'Rest & Consolidation',
        title: '3D Specimen Studio & Brain Rest',
        description:
          'You have fulfilled your daily practice quota! Inspect 3D marine fossils or rest your mind for optimal memory consolidation.',
        rationale: 'ADHD Cognitive Guard: Rest is when neural pathways consolidate learned concepts.',
        estimatedMinutes: 5,
        explorerPoints: 5,
        claweyQuote:
          'Superb scholarship today, Tudor! A true marine naturalist knows when the tide turns for resting.',
        urgency: 'rest',
      };
    }

    // Duration: 5 Minutes (Quick Dip)
    if (duration === 5) {
      if (tidePhase.phase === 'morning') {
        return {
          stationRoute: '#/daily-review',
          stationName: 'Daily Memory Review',
          domainLabel: 'SRS · Spaced Repetition',
          title: 'Morning 5-Question SRS Retrieval',
          description:
            'Quick morning memory check with ≤5 due vocabulary and maths cards before the school day starts.',
          rationale: 'Ebbinghaus Spaced Repetition: Quick morning recall combats forgetting curve decay.',
          estimatedMinutes: 5,
          explorerPoints: 10,
          claweyQuote:
            'A gentle 5-minute dip to wake up your naturalist senses! Let’s see what memory pearls are due.',
          urgency: streakStatus.isFrozen ? 'recovery' : 'routine',
        };
      }
      if (tidePhase.phase === 'night') {
        return {
          stationRoute: '#/gallery',
          stationName: 'Naturalist Studio',
          domainLabel: 'Tactile WebGL',
          title: 'Abyssal Specimen Turntable',
          description:
            'Rotate and inspect authentic 3D marine fossils in the quiet depths with zero timer pressure.',
          rationale: 'Calming bedtime transition: Visual exploration without adrenaline-inducing timers.',
          estimatedMinutes: 5,
          explorerPoints: 5,
          claweyQuote:
            'The abyssal depths are tranquil tonight. Inspect an ammonite and let your mind drift into restful sleep.',
          urgency: 'rest',
        };
      }
      return {
        stationRoute: '#/context-lens',
        stationName: 'Context Lens',
        domainLabel: 'Domain 2a & 2g',
        title: 'Bite-Sized Vocabulary Lens',
        description:
          'Unpack 2 high-level marine words with the tap-to-expand definition drawer and quick mastery checks.',
        rationale: 'Micro-learning: 5 minutes builds vocabulary nuance without cognitive strain.',
        estimatedMinutes: 5,
        explorerPoints: 10,
        claweyQuote:
          'Words are like rare shells — inspect their inner structure and you unlock their true meaning!',
        urgency: 'routine',
      };
    }

    // Duration: 10 Minutes (Standard Dive)
    if (duration === 10) {
      if (tidePhase.phase === 'afternoon') {
        return {
          stationRoute: '#/sprint',
          stationName: 'Quest Exam Sprint',
          domainLabel: 'Quest Paper 1 & 2',
          title: '10-Minute Bexley Quest Exam Sprint',
          description:
            'Fast-paced multiple-choice sprint covering Verbal Reasoning, Numerical, and Spatial NVR with visual disc timer.',
          rationale:
            'Post-school peak alertness: 10-minute game-like burst matches ADHD executive functioning perfectly.',
          estimatedMinutes: 10,
          explorerPoints: 20,
          claweyQuote:
            'Afternoon high tide is here! Let’s test your wits against the 10-minute Quest clock — Zen Mode is ready if you want it.',
          urgency: 'peak',
        };
      }
      if (tidePhase.phase === 'evening') {
        return {
          stationRoute: '#/read',
          stationName: 'Comprehension Station',
          domainLabel: 'Domain 2d · Inference',
          title: 'Clue Hunter: Evidence Finder',
          description:
            'Read an authentic ≤60-word lobster passage and tap the textual clue sentences to prove your inference.',
          rationale:
            'Evening immersion: Textual detective work without countdown pressure avoids bedtime cortisol spikes.',
          estimatedMinutes: 10,
          explorerPoints: 15,
          claweyQuote:
            'Every question hides a secret clue in the text. Be a detective and illuminate the proof!',
          urgency: 'routine',
        };
      }
      // Morning / Weekend 10m
      return {
        stationRoute: '#/spelling',
        stationName: 'Spelling Reef',
        domainLabel: 'SPAG · Phonics',
        title: 'Spelling Reef Phonics Drill',
        description:
          'Multisensory syllable chunking, nonsense-word decoding, and visual mnemonics for tricky 11+ spellings.',
        rationale: 'Structured synthetic phonics reinforces orthographic mapping for Year 4 candidates.',
        estimatedMinutes: 10,
        explorerPoints: 15,
        claweyQuote:
          'Crack the shells of tricky words into manageable syllables, just like a lobster cracking a crab!',
        urgency: 'routine',
      };
    }

    // Duration: 20 Minutes (Deep Expedition)
    if (duration === 20) {
      if (dayType === 'weekday' && tidePhase.phase === 'afternoon') {
        return {
          stationRoute: '#/scan',
          stationName: 'Schoolwork Scanner',
          domainLabel: 'Adaptive Diagnosis',
          title: 'Schoolwork Scanner & Remediation Drill',
          description:
            'Upload a photo of today’s school worksheet for instant KS2 gap analysis and 5 targeted practice questions.',
          rationale:
            'Targeted remediation: Directly bridges classroom misconceptions into immediate home mastery.',
          estimatedMinutes: 20,
          explorerPoints: 30,
          claweyQuote:
            'Show me your classroom worksheet! We shall diagnose any tricky spots and turn them into your greatest strengths.',
          urgency: 'peak',
        };
      }
      if (dayType === 'saturday' || dayType === 'sunday') {
        return {
          stationRoute: '#/geometry',
          stationName: 'Tactile Geometry',
          domainLabel: 'Domain 4G · Spatial',
          title: 'Tactile Geometry Deep Expedition',
          description:
            'Hands-on angle measurement with the Claw Protractor, Benthic Mirror Line reflections, and sonar coordinates.',
          rationale:
            'Tactile spatial reasoning: Concrete manipulation solidifies Non-Verbal & Spatial concepts.',
          estimatedMinutes: 20,
          explorerPoints: 30,
          claweyQuote:
            'Grab the Claw Protractor and chart our marine coordinates! Angles reveal the geometry of the sea.',
          urgency: 'routine',
        };
      }
      return {
        stationRoute: '#/sprint',
        stationName: 'Quest Exam Sprint',
        domainLabel: 'Interleaved Reasoning',
        title: 'Dual-Domain Interleaved Quest',
        description:
          'Two consecutive 10-minute sprints interleaving Numerical word problems with Non-Verbal spatial patterns.',
        rationale: 'Interleaving practice forces strategy discrimination and enhances long-term retention.',
        estimatedMinutes: 20,
        explorerPoints: 35,
        claweyQuote:
          'Switching gears between maths and spatial reasoning trains your brain for the real exam format!',
        urgency: 'peak',
      };
    }

    // Duration: 45 Minutes (Full Mock Simulation)
    return {
      stationRoute: '#/mock',
      stationName: 'Bexley Mock Exam',
      domainLabel: 'Quest Assessments Format',
      title: 'Full-Length Bexley Mock Exam',
      description:
        'Paper 1 (Verbal & English), 10-minute restorative breathing break, and Paper 2 (Maths & NVR) with OMR bubble sheets.',
      rationale:
        'Realistic stamina conditioning: Replicates the exact 2-paper Bexley Selection Test format and SAS 200 scale.',
      estimatedMinutes: 45,
      explorerPoints: 60,
      claweyQuote:
        'A grand examination voyage! Pace yourself steadily, mark your OMR bubbles with care, and remember: breathe during the break.',
      urgency: dayType === 'saturday' ? 'peak' : 'routine',
    };
  }

  /**
   * Generates secondary / alternative recommendations if Tudor prefers a different domain.
   */
  static getAlternativeRecommendations(
    selectedDuration: SessionDurationMinutes = 10,
    now = new Date()
  ): ActivityRecommendation[] {
    const primary = this.getRecommendation(selectedDuration, now);
    const candidates: ActivityRecommendation[] = [
      {
        stationRoute: '#/read',
        stationName: 'Comprehension Station',
        domainLabel: 'Domain 2d · Inference',
        title: 'Clue Hunter: Evidence Finder',
        description: 'Find clue sentences in short marine passages to prove inference answers.',
        rationale: 'Deep reading practice.',
        estimatedMinutes: 10,
        explorerPoints: 15,
        claweyQuote: 'The clues lie resting in plain sight.',
        urgency: 'routine',
      },
      {
        stationRoute: '#/sprint',
        stationName: 'Quest Exam Sprint',
        domainLabel: 'Paper 1 & 2',
        title: '10-Minute Quest Exam Sprint',
        description: 'Timed multi-choice challenge with disc timer and Socratic hints.',
        rationale: 'Bexley speed & accuracy.',
        estimatedMinutes: 10,
        explorerPoints: 20,
        claweyQuote: 'Test your reaction against the clock!',
        urgency: 'peak',
      },
      {
        stationRoute: '#/spelling',
        stationName: 'Spelling Reef',
        domainLabel: 'SPAG · Phonics',
        title: 'Spelling Reef Phonics Drill',
        description: 'Decode nonsense words and chunk tricky KS2 words.',
        rationale: 'Orthographic foundation.',
        estimatedMinutes: 10,
        explorerPoints: 15,
        claweyQuote: 'Phonics is the foundation of all reading.',
        urgency: 'routine',
      },
      {
        stationRoute: '#/geometry',
        stationName: 'Tactile Geometry',
        domainLabel: 'Domain 4G · Spatial',
        title: 'Tactile Marine Geometry',
        description: 'Interactive SVG Protractor & Mirror Line sandbox.',
        rationale: 'Spatial coordination.',
        estimatedMinutes: 10,
        explorerPoints: 15,
        claweyQuote: 'Navigate the geometric reefs with precision.',
        urgency: 'routine',
      },
      {
        stationRoute: '#/daily-review',
        stationName: 'Daily Review',
        domainLabel: 'SRS Memory',
        title: '5-Minute Memory Refresh',
        description: 'Clear today’s due spaced repetition review cards.',
        rationale: 'Daily memory consolidation.',
        estimatedMinutes: 5,
        explorerPoints: 10,
        claweyQuote: 'Small daily steps build monumental knowledge.',
        urgency: 'routine',
      },
    ];

    return candidates.filter((c) => c.stationRoute !== primary.stationRoute).slice(0, 3);
  }
}
